import { Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import type { DateValidationError } from "@mui/x-date-pickers-pro";
import { useMemo, useState } from "react";
import BcDatePicker from "@entrilia/shared/components/reporting/bc-datepicker/BcDatePicker";
import { modeOptions } from "../../sv/components/viewer/common/conditions/DateFilter/dateFilterOptions";
import type { DateMode, ExactDateRange } from "../../sv/components/viewer/common/conditions/DateFilter/dateFilterState";
import ReferenceDateList from "../../sv/components/viewer/common/conditions/DateFilter/ReferenceDateList";
import { formatToISODate, getValidatedDate } from "../../utils/dateUtils";
import type { RelativeDateValue } from "../../utils/relativeDates";
import { isRelativeDateValue } from "../../utils/relativeDates";

interface Props {
  kind: "AsOfDate" | "Period";
  values: string[];
  onChange: (values: string[]) => void;
  onClose: () => void;
}

const getInitialMode = (kind: Props["kind"], firstValue: string | undefined): DateMode => {
  if (isRelativeDateValue(firstValue)) {
    return "reference";
  }
  return kind === "Period" && firstValue === undefined ? "reference" : "exact";
};

const DateFilterEditor = ({ kind, values, onChange, onClose }: Props) => {
  const [firstValue, secondValue] = values;
  const [mode, setMode] = useState<DateMode>(() => getInitialMode(kind, firstValue));
  const exactDate = useMemo(() => getValidatedDate(firstValue), [firstValue]);
  const [exactRange, setExactRange] = useState<ExactDateRange>(() => [
    getValidatedDate(firstValue),
    getValidatedDate(secondValue),
  ]);

  const handleReferenceSelect = (value: RelativeDateValue) => {
    onChange([value]);
    onClose();
  };

  const handleExactDateChange = (date: Date | null, validationError: DateValidationError | null) => {
    if (date && !validationError) {
      onChange([formatToISODate(date)]);
    }
  };

  const handleExactRangeChange = (range: ExactDateRange) => {
    setExactRange(range);
    const [from, to] = range;
    if (from && to && from <= to) {
      onChange([formatToISODate(from), formatToISODate(to)]);
    }
  };

  return (
    <Stack spacing={2} sx={(t) => ({ p: 2, width: t.spacing(47.5) })}>
      <ToggleButtonGroup
        exclusive
        fullWidth
        color="primary"
        value={mode}
        onChange={(_, value: DateMode | null) => {
          if (value !== null) {
            setMode(value);
          }
        }}
      >
        {modeOptions.map((item) => (
          <ToggleButton key={item.value} value={item.value}>
            {item.caption}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {mode === "reference" && (
        <ReferenceDateList
          selectedValue={isRelativeDateValue(firstValue) ? firstValue : undefined}
          hideDays={kind === "Period"}
          hidePeriods={kind === "AsOfDate"}
          onSelect={handleReferenceSelect}
        />
      )}
      {mode === "exact" && kind === "AsOfDate" && (
        <BcDatePicker value={exactDate} autoFocus required onChange={handleExactDateChange} />
      )}
      {mode === "exact" && kind === "Period" && (
        <Stack direction="row" spacing={1}>
          <BcDatePicker
            value={exactRange[0]}
            maxDate={exactRange[1] ?? undefined}
            autoFocus
            required
            onChange={(date, validationError) => handleExactRangeChange([validationError ? null : date, exactRange[1]])}
          />
          <BcDatePicker
            value={exactRange[1]}
            minDate={exactRange[0] ?? undefined}
            required
            onChange={(date, validationError) => handleExactRangeChange([exactRange[0], validationError ? null : date])}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default DateFilterEditor;
